import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, UploadIcon, StarIcon, MoreHorizontalIcon } from 'lucide-react';
interface RoomImage {
  id: string;
  url: string;
  caption: string;
  uploadedBy: string;
  uploadDate: string;
  isActive: boolean;
  isFeatured?: boolean;
}
interface RoomAssetsProps {
  roomId: string;
  roomName: string;
  images: RoomImage[];
  onBack: () => void;
  onUpload: (files: FileList) => void;
  onUpdateImage: (imageId: string, updates: Partial<RoomImage>) => void;
  onDeleteImage: (imageId: string) => void;
}
export function RoomAssets({
  roomId,
  roomName,
  images,
  onBack,
  onUpload,
  onUpdateImage,
  onDeleteImage
}: RoomAssetsProps) {
  const {
    t
  } = useTranslation('rooms');
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('media');
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onUpload(e.dataTransfer.files);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files);
    }
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedImages(images.map(img => img.id));
    } else {
      setSelectedImages([]);
    }
  };
  const handleSelectImage = (imageId: string, checked: boolean) => {
    if (checked) {
      setSelectedImages([...selectedImages, imageId]);
    } else {
      setSelectedImages(selectedImages.filter(id => id !== imageId));
    }
  };
  return <div className="space-y-6" data-id="element-9288">
      <div className="flex items-center" data-id="element-9289">
        <button onClick={onBack} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-9290">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-9291" />
          <span data-id="element-9292">{t('components.RoomAssets.room_assets')}</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-9293">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-9294">
          <nav className="flex" role="tablist" aria-label="Room form sections" data-id="element-9295">
            <button role="tab" aria-selected={activeTab === 'info'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-9296">
              {t('components.EditRoomForm.room_information')}
            </button>
            <button role="tab" aria-selected={activeTab === 'media'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('media')} data-id="element-9297">
              {t('components.EditRoomForm.media_assets')}
            </button>
          </nav>
        </div>
        {/* Media Assets Content */}
        <div className="p-6" data-id="element-9298">
          {/* File Upload Area */}
          <div className={`border-2 border-dashed rounded-lg p-8 mb-6 flex flex-col items-center justify-center ${dragActive ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop} data-id="element-9299">
            <div className="p-3 bg-red-100 rounded-full mb-4" data-id="element-9300">
              <UploadIcon size={24} className="text-red-500" data-id="element-9301" />
            </div>
            <p className="text-gray-600 mb-1" data-id="element-9302">
              {t('components.RoomAssets.drag_files_here')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-9303">
              {t('components.RoomAssets.supported_formats_png_jpg_jpeg')}
            </p>
            <p className="text-xs text-gray-500 mb-4" data-id="element-9304">
              {t('components.RoomAssets.max_file_size_10mb')}
            </p>
            <label className="cursor-pointer" data-id="element-9305">
              <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={handleFileChange} multiple data-id="element-9306" />

              <span className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700" data-id="element-9307">
                {t('components.RoomAssets.upload')}
              </span>
            </label>
          </div>
          {/* Images Table */}
          <div className="overflow-x-auto" data-id="element-9308">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-9309">
              <thead data-id="element-9310">
                <tr data-id="element-9311">
                  <th className="w-12 py-3 pl-4" data-id="element-9312">
                    <input type="checkbox" checked={selectedImages.length === images.length && images.length > 0} onChange={handleSelectAll} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-9313" />
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9314">
                    {t('components.RoomAssets.room_image')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9315">
                    {t('components.RoomAssets.caption')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9316">
                    {t('components.RoomAssets.uploaded_by')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9317">
                    {t('components.RoomAssets.uploaded')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9318">
                    {t('components.RoomAssets.visibility')}
                  </th>
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9319">
                    {t('components.RoomAssets.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-9320">
                {images.map(image => <tr key={image.id} data-id="element-9321">
                    <td className="py-4 pl-4" data-id="element-9322">
                      <input type="checkbox" checked={selectedImages.includes(image.id)} onChange={e => handleSelectImage(image.id, e.target.checked)} className="h-4 w-4 text-red-600 border-gray-300 rounded" data-id="element-9323" />
                    </td>
                    <td className="py-4" data-id="element-9324">
                      <div className="w-24 h-16 bg-gray-100 rounded overflow-hidden" data-id="element-9325">
                        <img src={image.url} alt={image.caption || 'Room image'} className="w-full h-full object-cover" data-id="element-9326" />
                      </div>
                    </td>
                    <td className="py-4" data-id="element-9327">
                      <div className="flex items-center" data-id="element-9328">
                        {image.isFeatured && <StarIcon size={16} className="text-yellow-400 mr-1" data-id="element-9329" />}
                        <span data-id="element-9330">{image.caption || 'Untitled'}</span>
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-9331">
                      {image.uploadedBy}
                    </td>
                    <td className="py-4 text-sm text-gray-500" data-id="element-9332">
                      {image.uploadDate}
                    </td>
                    <td className="py-4" data-id="element-9333">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${image.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9334">
                        {image.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-4" data-id="element-9335">
                      <button aria-label="Image actions" className="text-gray-400 hover:text-gray-500" data-id="element-9336">
                        <MoreHorizontalIcon size={16} aria-hidden="true" data-id="element-9337" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}